// Two-way texting (owner request 2026-10-03): the /api/sms calls and the small pure helpers shared by the Texts page
// (pages/Texts.tsx) and the call screen's Text panel (dialer/TextPanel.tsx).
import type {
  SmsAssistDraft, SmsAssistRequest, SmsAssistResponse, SmsAssistSummary, SmsBusinessView, SmsMessage, SmsNewTextView,
  SmsSendRequest, SmsSendResponse, SmsThreadDetail, SmsThreadItem, SmsUnread,
} from '../../../src/shared/api-sms';
import { SMS_CANADA_OPT_OUT_LINE } from '../../../src/shared/api-sms';
import { areaCodeRegion } from '../../../src/shared/area-codes';
import { api, ApiFailure } from '../api';
import { isTypingTarget, type TargetLike } from './hotkeys';
import { fmtPhone } from './labels';

export const smsApi = {
  threads: (scope: 'mine' | 'all', unreadOnly = false) =>
    api.get<SmsThreadItem[]>(`/api/sms/threads?scope=${scope}${unreadOnly ? '&unread=1' : ''}`),
  thread: (id: number) => api.get<SmsThreadDetail>(`/api/sms/threads/${id}`),
  business: (businessId: number) => api.get<SmsBusinessView>(`/api/sms/business/${businessId}`),
  unread: () => api.get<SmsUnread>('/api/sms/unread'),
  /** New text (Texts page): what sending to this number would do, before sending. */
  newText: (to: string, businessId: number | null) =>
    api.get<SmsNewTextView>(`/api/sms/new-text?to=${encodeURIComponent(to)}${businessId ? `&businessId=${businessId}` : ''}`),
  send: (req: SmsSendRequest) => api.post<SmsSendResponse>('/api/sms/send', req),
  link: (threadId: number, businessId: number) => api.post<SmsThreadDetail>(`/api/sms/threads/${threadId}/link`, { businessId }),
  assign: (threadId: number, userId: number | null) => api.post<SmsThreadDetail>(`/api/sms/threads/${threadId}/assign`, { userId }),
  /** AI help (2026-10-06): the next text, or where the conversation stands. Never sends anything. */
  assist: (threadId: number, req: SmsAssistRequest) => api.post<SmsAssistResponse>(`/api/sms/threads/${threadId}/assist`, req),
  /** The first text to a lead with no conversation with that number yet. */
  assistBusiness: (businessId: number, req: SmsAssistRequest) => api.post<SmsAssistResponse>(`/api/sms/business/${businessId}/assist`, req),
};

// ---------- AI help in the composer (owner request, PJ, 2026-10-06) ----------

/** What the composer's "AI draft" and "Summarize" call. `summarize` is null where there is nothing to summarize. */
export interface SmsAssistActions {
  draft: (instructions: string) => Promise<SmsAssistDraft>;
  summarize: (() => Promise<SmsAssistSummary>) | null;
}

function asDraft(r: SmsAssistResponse): SmsAssistDraft {
  if (r.mode !== 'draft') throw new Error('The AI sent back a summary instead of a draft. Try again.');
  return r;
}
function asSummary(r: SmsAssistResponse): SmsAssistSummary {
  if (r.mode !== 'summary') throw new Error('The AI sent back a draft instead of a summary. Try again.');
  return r;
}

/** The AI help for one conversation (its id) or, with no conversation yet, for one number of a lead (the first text
 *  after a call). null = no AI help here (no conversation and no lead: nothing to go on). */
export function smsAssistFor(a: { threadId: number | null; businessId: number | null; to: string | null; canSummarize: boolean }): SmsAssistActions | null {
  const words = (instructions: string): { instructions?: string } => (instructions.trim() ? { instructions: instructions.trim() } : {});
  if (a.threadId !== null) {
    const id = a.threadId;
    return {
      draft: async (instructions) => asDraft(await smsApi.assist(id, { mode: 'draft', ...words(instructions) })),
      summarize: a.canSummarize ? async () => asSummary(await smsApi.assist(id, { mode: 'summary' })) : null,
    };
  }
  if (a.businessId !== null && a.to) {
    const businessId = a.businessId;
    const to = a.to;
    return { draft: async (instructions) => asDraft(await smsApi.assistBusiness(businessId, { mode: 'draft', to, ...words(instructions) })), summarize: null };
  }
  return null;
}

/** Where an AI draft goes (pure): an empty box takes it; what the setter typed is replaced only after "Replace what you
 *  typed?". null = keep what they typed. */
export function aiDraftInto(current: string, draft: string, ask: (question: string) => boolean): string | null {
  if (!current.trim()) return draft;
  return ask('Replace what you typed with the AI draft?') ? draft : null;
}

/** Sends; a 409 late_hour (outside 8 AM-9 PM their time, a recommendation) asks "Send anyway?" once and resends with
 *  confirmLate. Returns null when the setter says no. Every other error is thrown for the caller to show. */
export async function sendText(req: SmsSendRequest, ask: (question: string) => boolean = (q) => window.confirm(q)): Promise<SmsSendResponse | null> {
  try {
    return await smsApi.send(req);
  } catch (e) {
    if (e instanceof ApiFailure && e.status === 409 && e.body.error === 'late_hour') {
      if (!ask(`${e.body.message} Send anyway?`)) return null;
      return smsApi.send({ ...req, confirmLate: true });
    }
    throw e;
  }
}

// ---------- the call screen's Text button: is this lead textable at all? ----------
// GET /api/sms/business/:id `blocked` (a rule that never lifts, or a calling block when none of its numbers ever texted
// us). Remembered for a minute per business, and refreshed whenever the Text panel reloads the view.
const SMS_BLOCK_TTL_MS = 60_000;
const smsBlocks = new Map<number, { blocked: string | null; at: number }>();
const smsBlockSubs = new Set<(businessId: number, blocked: string | null) => void>();

export function rememberSmsBlock(businessId: number, blocked: string | null, now: number = Date.now()): void {
  smsBlocks.set(businessId, { blocked, at: now });
  for (const f of smsBlockSubs) f(businessId, blocked);
}

/** The remembered answer, while fresh; null = unknown (ask the server). */
export function knownSmsBlock(businessId: number, now: number = Date.now()): { blocked: string | null } | null {
  const hit = smsBlocks.get(businessId);
  return hit && now - hit.at < SMS_BLOCK_TTL_MS ? { blocked: hit.blocked } : null;
}

export function onSmsBlock(f: (businessId: number, blocked: string | null) => void): () => void {
  smsBlockSubs.add(f);
  return () => { smsBlockSubs.delete(f); };
}

/** Asks the server once (per minute) whether this lead can be texted; the answer arrives through onSmsBlock. */
export function loadSmsBlock(businessId: number): void {
  if (knownSmsBlock(businessId)) return;
  smsApi.business(businessId).then((v) => rememberSmsBlock(businessId, v.blocked), () => { /* the panel shows the error */ });
}

/** The server refused because the first text to this number needs the consent tick (the composer then shows it). */
export const isConsentNeeded = (e: unknown): boolean => e instanceof ApiFailure && e.status === 409 && e.body.error === 'consent_needed';

/** US texts are still pending carrier approval (setting sms_us_a2p_approved false) and this text is US: the recipient's
 *  or our sending number's area code is American. */
export function usTextPending(approved: boolean, ...numbers: Array<string | null | undefined>): boolean {
  if (approved) return false;
  return numbers.some((e164) => !!e164 && /^\+1\d{10}$/.test(e164) && areaCodeRegion(e164.slice(2, 5))?.country === 'US');
}

/** The toast after a send: Twilio's refusal; "Unconfirmed" when Twilio didn't answer in time; "queued" while US texts
 *  are pending approval (no "sent" until it is Delivered); else "Text sent". */
export function sendToast(r: SmsSendResponse, to: string, usPending: boolean): { text: string; kind: 'ok' | 'bad' | 'warn' | 'info' } {
  if (!r.ok) return { text: r.message.errorText ?? 'Not sent.', kind: 'bad' };
  if (r.message.status === 'queued' && r.message.errorText) return { text: r.message.errorText, kind: 'warn' };
  if (usPending) return { text: 'Text queued… watch for Delivered', kind: 'info' };
  return { text: `Text sent to ${fmtPhone(to)}.`, kind: 'ok' };
}

/** Whether the composer takes the cursor now (pure; SmsConversation.tsx runs it after each render). Only a composer
 *  asked to (autoFocus: the call screen's Text panel, opened with T or the Text button), only when it is enabled and just
 *  became enabled or switched to another number, and never away from another box the setter is typing in. Typing in
 *  the message box itself never reaches the dialer's hotkeys (hotkeys.ts isTypingTarget: a TEXTAREA is a typing target). */
export function composerTakesFocus(a: { autoFocus: boolean; enabled: boolean; changed: boolean; active: TargetLike | null; activeIsComposer: boolean }): boolean {
  if (!a.autoFocus || !a.enabled || !a.changed) return false;
  return a.activeIsComposer || !isTypingTarget(a.active);
}

/** Where the cursor waits while an AI draft or summary loads (pure; SmsConversation.tsx runs it right before the wait
 *  starts, then the buttons go busy). The dialer's hotkeys must never fire meanwhile: a setter who keeps typing would
 *  otherwise hang up (H), dial (Space) or save (Enter). A box the setter is typing in keeps it ("Tell the AI what to
 *  say" turns read-only for the wait, never disabled); anything else (the AI draft or Summarize button just clicked)
 *  hands it to the message box before that button is disabled. 'stay' with no typing box at all only when the message
 *  box is disabled (a conversation that can't send, where only Summarize is offered and nothing is being typed). */
export function aiWaitFocus(a: { active: TargetLike | null; messageBoxUsable: boolean }): 'stay' | 'message' {
  if (isTypingTarget(a.active)) return 'stay';
  return a.messageBoxUsable ? 'message' : 'stay';
}

/** The word under an outbound text. */
export function smsStatusLabel(m: Pick<SmsMessage, 'direction' | 'status'>): string {
  if (m.direction === 'in') return '';
  switch (m.status) {
    case 'queued': return 'Queued';
    case 'sent': return 'Sent';
    case 'delivered': return 'Delivered';
    case 'undelivered': return 'Not delivered';
    case 'failed': return 'Failed';
    default: return '';
  }
}

export const smsStatusTone = (m: Pick<SmsMessage, 'status'>): 'ok' | 'bad' | 'idle' =>
  m.status === 'delivered' ? 'ok' : m.status === 'failed' || m.status === 'undelivered' ? 'bad' : 'idle';

/** Who a conversation is with: the business name, else their number. */
export const threadTitle = (t: Pick<SmsThreadItem, 'business' | 'theirE164'>): string => t.business?.name || fmtPhone(t.theirE164);

/** Already says how to opt out (so the Canada hint goes away). */
export const hasOptOutLine = (body: string): boolean => /\bstop\b/i.test(body);

/** Appends the Canadian opt-out line as its own sentence. After a link it goes on a new line: a period right after a
 *  URL can end up inside the link on some phones. */
export function withOptOutLine(body: string): string {
  const t = body.replace(/\s+$/, '');
  if (!t) return SMS_CANADA_OPT_OUT_LINE;
  if (/https?:\/\/\S+$/i.test(t)) return `${t}\n${SMS_CANADA_OPT_OUT_LINE}`;
  return `${t}${/[.!?]$/.test(t) ? '' : '.'} ${SMS_CANADA_OPT_OUT_LINE}`;
}

/** Puts `text` at the cursor (start..end), with a space either side when it would touch a word. */
export function insertAtCursor(body: string, text: string, start: number, end: number): { body: string; cursor: number } {
  const s = Math.max(0, Math.min(start, body.length));
  const e = Math.max(s, Math.min(end, body.length));
  const before = body.slice(0, s);
  const after = body.slice(e);
  const pre = before && !/\s$/.test(before) ? ' ' : '';
  const post = after && !/^\s/.test(after) ? ' ' : '';
  const next = `${before}${pre}${text}${post}${after}`;
  return { body: next, cursor: before.length + pre.length + text.length + post.length };
}

/** '2:05 PM' today, 'Mon Oct 5, 2:05 PM' otherwise, in the setter's own zone. */
export function smsTime(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
